import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Car, Tag, Power, Inbox, HandCoins, Search, Mail, CalendarClock, RefreshCw, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { FEATURES } from "@/lib/company";

const KPI = ({ icon: Icon, label, value, today, testId }) => (
  <div className="rounded-xl border border-line bg-surface p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-elevated text-brand"><Icon size={15} strokeWidth={1.8} /></span>
      {today > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700">+{today} heute</span>}
    </div>
    <p className="font-display font-bold text-3xl text-ink mt-4">{value}</p>
    <p className="text-xs text-faint mt-1 font-medium">{label}</p>
  </div>
);

const SYNC_LABEL = { success: "Erfolgreich", partial: "Teilweise", failed: "Fehlgeschlagen", not_configured: "Keine Quelle" };

export const AdminDashboard = ({ onOpenSync }) => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setError(true));
  }, []);

  if (error) return <p className="text-sm text-red-600 py-8">Statistiken konnten nicht geladen werden.</p>;
  if (!stats) return <div className="py-16 flex justify-center"><div className="w-7 h-7 border-2 border-line border-t-brand rounded-full animate-spin" /></div>;

  const t = stats.today.by_type;
  const typeKpis = [
    ...(FEATURES.vehicleForms ? [
      { icon: Mail, label: "Fahrzeuganfragen", value: stats.by_type.fahrzeuganfrage || 0, today: t.fahrzeuganfrage, id: "kpi-fahrzeuganfrage" },
      { icon: CalendarClock, label: "Besichtigungen", value: stats.by_type.besichtigung || 0, today: t.besichtigung, id: "kpi-besichtigung" },
    ] : []),
    ...(FEATURES.ankauf ? [{ icon: HandCoins, label: "Ankauf", value: stats.by_type.ankauf || 0, today: t.ankauf, id: "kpi-ankauf" }] : []),
    ...(FEATURES.findet ? [{ icon: Search, label: "Wunschfahrzeug", value: stats.by_type.findet || 0, today: t.findet, id: "kpi-findet" }] : []),
  ];
  const kpis = [
    { icon: Car, label: "Fahrzeuge online", value: stats.vehicles_online, id: "kpi-online" },
    { icon: Power, label: "Inaktiv", value: stats.vehicles_inactive, id: "kpi-inactive" },
    { icon: Tag, label: "Verkauft", value: stats.vehicles_sold, id: "kpi-sold" },
    { icon: Inbox, label: "Anfragen gesamt", value: stats.leads_total, today: stats.today.leads, id: "kpi-leads" },
    ...(typeKpis.length ? [...typeKpis, { icon: Mail, label: "Kontakt", value: stats.by_type.kontakt || 0, today: t.kontakt, id: "kpi-kontakt" }] : []),
  ].slice(0, 8);
  const ls = stats.last_sync;

  return (
    <div data-testid="admin-stats-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => <KPI key={k.id} testId={k.id} {...k} />)}
      </div>

      <button onClick={onOpenSync} data-testid="admin-last-sync-card"
        className="w-full text-left flex items-center gap-4 rounded-xl border border-line bg-surface px-5 py-4 mt-6 hover:border-silver/40 transition-colors">
        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-brand text-brand-ink shrink-0"><RefreshCw size={15} /></span>
        <div className="min-w-0">
          <p className="text-[10px] text-faint font-semibold">Letzter erfolgreicher Abgleich</p>
          <p className="text-sm text-ink mt-0.5">
            {ls ? `${new Date(ls.finished_at).toLocaleString("de-AT")} · ${SYNC_LABEL[ls.status] || ls.status} · +${ls.added} neu · ${ls.updated} aktualisiert · ${ls.deactivated} deaktiviert`
              : "Noch kein Abgleich durchgeführt"}
          </p>
        </div>
        <ArrowRight size={16} className="ml-auto text-dim shrink-0" />
      </button>

      <div className="rounded-xl border border-line bg-surface p-6 mt-6" data-testid="admin-30d-chart">
        <p className="text-[10px] uppercase tracking-[0.2em] text-faint font-semibold mb-5">Anfragen – letzte 30 Tage</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.timeseries} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C81820" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#C81820" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E7E9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8E9096" }} tickLine={false} axisLine={false}
                tickFormatter={(d) => `${d.slice(8)}.${d.slice(5, 7)}.`} interval={4} />
              <YAxis tick={{ fontSize: 10, fill: "#8E9096" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString("de-AT")}
                contentStyle={{ borderRadius: 10, border: "1px solid #E2E2E2", background: "#FFFFFF", color: "#242424", fontSize: 12 }} />
              <Area type="monotone" dataKey="leads" name="Anfragen" stroke="#C81820" strokeWidth={2} fill="url(#gLeads)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
